import { useRef, useState, type JSX } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { MIN_OUTCOMES_TO_JUDGE, type GatePerformance } from '@profiler/shared'
import type { OutcomeImportResult } from '../api'
import { EmptyState, ErrorPanel, LoadingPanel } from '../components/States'
import { PageHeader } from '../components/Page'
import { formatDateTime, formatNumber, humanise, precisionTone, splitCamelCase } from '../format'
import { qk, useOutreachFeedback } from '../queries'
import { useApi } from '../auth'
import { usePageTitle } from '../usePageTitle'

/**
 * Whether the gates are right, as opposed to merely consistent.
 *
 * Every rule in the profiler is a guess made before sending: is this the right
 * person, and is this a live mailbox at the right company. Both are answered for
 * free within hours of a campaign — a bounce says the address was wrong, an
 * out-of-office says the person was, a reply says the whole chain was right.
 * Nothing captured those, so the thresholds and the seniority weights and the
 * domain lists could only be argued about, and "credits per usable contact" was
 * unfalsifiable because "usable" meant "our own gates approved it".
 *
 * This screen is that loop closed: drop in what the sending platform reports,
 * and every rule gets a mark.
 */

const ACCEPTED = ['.csv', '.tsv', '.txt', '.xlsx', '.xls'] as const

/**
 * The route refuses a body over 6MB and the multipart framing counts towards
 * it, so the file itself is held below that. Checked here because the only
 * thing an oversized platform export got back was a bare 413, after the whole
 * upload had gone over the wire.
 */
const MAX_BYTES = 5 * 1024 * 1024

/** A size in the units the file was exported in, so it can be recognised. */
function fileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** How an outcome found the company it belongs to, worst-to-best in plain words. */
const ATTRIBUTION_LABELS: Record<string, string> = {
  company: 'a company column',
  email_domain: 'the address domain',
  newest: 'their newest company (a guess)',
}

/**
 * A readable name for each rule the feedback report scores.
 *
 * Every facet `measureFeedback` emits has an entry. One that does not — a facet
 * added to the backend and not here — falls back to a humanised field name
 * rather than the camelCase identifier, which is what leaked onto this screen
 * as "emailConfidence" sitting between two sentences of plain English.
 */
const FACET_LABELS: Record<string, string> = {
  matchBasis: 'How the company was matched',
  seniorityBand: 'Seniority',
  gateOutcome: 'Review outcome',
  gateReason: 'Gate reason',
  emailConfidence: 'Address confidence',
}

function PrecisionCell({ row }: Readonly<{ row: GatePerformance }>): JSX.Element {
  /*
   * Any rate that is not a real number is "not yet judged", not zero.
   *
   * The guard was `=== null`, which is the value the contract documents — and
   * anything else, an older server or a facet with no denominator, arrived as
   * `undefined`, multiplied to NaN, and printed "NaN%" beside a bar at FULL
   * length in error red. A missing measurement rendered as the worst possible
   * measurement, on the one screen whose whole job is deciding which rules to
   * delete.
   */
  if (typeof row.precision !== 'number' || !Number.isFinite(row.precision)) {
    return (
      <Tooltip
        title={`Needs ${MIN_OUTCOMES_TO_JUDGE} mailed contacts before a rate means anything. ${row.mailed} so far.`}
      >
        <Typography variant="body2" color="text.secondary">
          Not yet judged
        </Typography>
      </Tooltip>
    )
  }
  // Red below four in five: at that point the rule is doing more harm than the
  // review time it saves.
  const colour = precisionTone(row.precision, 'success.main', 'warning.main', 'error.main')
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ color: colour, fontVariantNumeric: 'tabular-nums', minWidth: 48 }}>
        {(row.precision * 100).toFixed(0)}%
      </Typography>
      <LinearProgress
        variant="determinate"
        value={row.precision * 100}
        sx={{ width: 90, height: 6, borderRadius: 3 }}
        color={precisionTone(row.precision, 'success', 'warning', 'error')}
      />
    </Stack>
  )
}

/** A delivery outcome as a colour: bad news, good news, or neither. */
function outcomeTone(outcome: string): 'error' | 'success' | 'default' {
  if (outcome === 'bounced' || outcome === 'left_company') return 'error'
  return outcome === 'replied' ? 'success' : 'default'
}

export function OutreachPage(): JSX.Element {
  usePageTitle('Outreach')

  const api = useApi()
  const queryClient = useQueryClient()
  const feedback = useOutreachFeedback()
  const fileInput = useRef<HTMLInputElement>(null)
  const [result, setResult] = useState<OutcomeImportResult | null>(null)
  const [chosen, setChosen] = useState<File | null>(null)
  const [rejected, setRejected] = useState<string | null>(null)

  const importOutcomes = useMutation({
    mutationFn: (file: File) => api.importOutcomes(file),
    onSuccess: async (res) => {
      setChosen(null)
      setResult(res)
      await queryClient.invalidateQueries({ queryKey: qk.outreachFeedback })
    },
  })

  /*
   * Picked, not imported.
   *
   * An import stamps `deliveryOutcome` on every employment row it matches and
   * no route clears one, so the wrong file chosen in the OS dialog could only
   * be repaired by importing a correct file that happens to name the same
   * people. It costs one click to name the file back first.
   */
  function choose(file: File | undefined): void {
    setRejected(null)
    if (!file) return
    const lower = file.name.toLowerCase()
    if (!ACCEPTED.some((ext) => lower.endsWith(ext))) {
      setRejected(`${file.name} is not a CSV or spreadsheet.`)
      return
    }
    if (file.size > MAX_BYTES) {
      setRejected(`${file.name} is ${fileSize(file.size)}. The limit is 5 MB.`)
      return
    }
    setChosen(file)
  }

  const data = feedback.data

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Outreach"
        description="What happened after the send, and what it says about the rules that chose who to send to."
      />

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">Import outcomes</Typography>
              <Typography variant="body2" color="text.secondary">
                A CSV or spreadsheet from whatever you send with. It needs an email column and an outcome
                column — bounced, replied, unsubscribed, delivered, out of office. Anything it cannot read is
                reported rather than guessed at.
              </Typography>
              {/*
                A mail platform knows an address, and an address identifies a
                person, not which of their employers we mailed them about. Two
                things narrow it, and the result says which was used.
              */}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                An outcome is filed against the company it was really about: a company column if the file has
                one, otherwise the address’s own domain. Include a company column and every outcome lands
                exactly.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={importOutcomes.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {importOutcomes.isPending ? 'Importing…' : 'Choose file'}
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls"
              hidden
              onChange={(event) => {
                choose(event.target.files?.[0])
                event.target.value = ''
              }}
            />
          </Stack>

          {rejected ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {rejected}
            </Alert>
          ) : null}

          {chosen ? (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={importOutcomes.isPending}
                    onClick={() => setChosen(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    disabled={importOutcomes.isPending}
                    onClick={() => importOutcomes.mutate(chosen)}
                  >
                    {importOutcomes.isPending ? 'Importing…' : 'Import'}
                  </Button>
                </Stack>
              }
            >
              {chosen.name} · {fileSize(chosen.size)}. The outcomes in it are written straight onto the
              contacts it names and nothing here can take them back, so check it is the export you meant.
            </Alert>
          ) : null}

          {importOutcomes.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(importOutcomes.error as Error).message}
            </Alert>
          ) : null}

          {result ? (
            <Alert severity={result.applied > 0 ? 'success' : 'warning'} sx={{ mt: 2 }}>
              <Typography variant="body2">
                {formatNumber(result.applied)} of {formatNumber(result.rows)} rows recorded.
              </Typography>
              {Object.keys(result.attribution ?? {}).length > 0 ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Company identified by{' '}
                  {Object.entries(result.attribution)
                    .map(([how, n]) => `${ATTRIBUTION_LABELS[how] ?? how}: ${formatNumber(n)}`)
                    .join(', ')}
                  .
                </Typography>
              ) : null}
              {result.unmatched.length > 0 ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  No contact holds {result.unmatched.length === 1 ? 'this address' : 'these addresses'}:{' '}
                  {result.unmatched.slice(0, 5).join(', ')}
                  {result.unmatched.length > 5 ? ` and ${result.unmatched.length - 5} more` : ''}
                </Typography>
              ) : null}
              {result.unreadable.length > 0 ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Outcome not recognised, so left alone: {result.unreadable.slice(0, 3).join('; ')}
                </Typography>
              ) : null}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {feedback.isLoading ? <LoadingPanel /> : null}
      {feedback.isError ? (
        <ErrorPanel error={feedback.error} onRetry={() => void feedback.refetch()} />
      ) : null}

      {data?.mailed === 0 ? (
        <Card>
          <EmptyState
            title="Nothing has been mailed yet"
            description="Import what your sending platform reports and every rule in the profiler gets marked against what actually landed — which is the only way to tell a gate that works from one that is merely consistent."
          />
        </Card>
      ) : null}

      {data && data.mailed > 0 ? (
        <>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(data.mailed)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    contacts with an outcome
                  </Typography>
                </Box>
                {Object.entries(data.byOutcome).map(([outcome, count]) => (
                  <Chip
                    key={outcome}
                    label={`${humanise(outcome)}: ${formatNumber(count)}`}
                    color={outcomeTone(outcome)}
                    variant="outlined"
                  />
                ))}
                <Box sx={{ flexGrow: 1 }} />
                {data.lastOutcomeAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Newest outcome {formatDateTime(data.lastOutcomeAt)}
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2">How each rule performed</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                The share of contacts a rule let through that did not bounce and had not left. Worst first,
                because that is the one to change. An unsubscribe does not count against a rule — not wanting
                to hear from us is not the same as us having the wrong person.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell align="right">Mailed</TableCell>
                      <TableCell align="right">Bounced</TableCell>
                      <TableCell align="right">Left</TableCell>
                      <TableCell align="right">Replied</TableCell>
                      <TableCell>Held up</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.facets.map((row) => (
                      <TableRow key={`${row.facet}:${row.value}`} hover>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {FACET_LABELS[row.facet] ?? humanise(splitCamelCase(row.facet))}
                          </Typography>
                        </TableCell>
                        <TableCell>{humanise(row.value)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatNumber(row.mailed)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.bounced > 0 ? formatNumber(row.bounced) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.leftCompany > 0 ? formatNumber(row.leftCompany) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.replied > 0 ? formatNumber(row.replied) : '—'}
                        </TableCell>
                        <TableCell>
                          <PrecisionCell row={row} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </>
      ) : null}
    </Stack>
  )
}
